import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Clock, MapPin, CheckCircle2, XCircle, AlertCircle, Users,
  LogIn, LogOut, Calendar, DollarSign, TrendingUp, Timer,
  RefreshCw, Download, ChevronDown, ChevronUp, Plus, FileText, Settings
} from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = 'emerald' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : 'card'}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-2xl font-bold text-text-primary mt-1">{value}</p>
          {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", `bg-${color}-100`)}>
          <Icon className={cn("h-5 w-5", `text-${color}-600`)} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── GPS Auto Check-in Widget ─────────────────────────────────────────────────
function AutoCheckInWidget({ onSuccess }) {
  const [status, setStatus] = useState('idle'); // idle, detecting, success, error, disabled
  const [message, setMessage] = useState('');
  const [zone, setZone] = useState('');

  const attemptAutoCheckin = useCallback(async () => {
    if (!navigator.geolocation) {
      setStatus('error');
      setMessage('Geolocation not supported');
      return;
    }
    setStatus('detecting');
    setMessage('Detecting your location...');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await axios.post(`${API}/api/hr/attendance/auto-checkin`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          if (data.ok && data.auto_checkin) {
            setStatus('success');
            setZone(data.zone);
            setMessage(`Auto checked in at ${data.zone} (${data.distance_m}m)`);
            toast.success(`Auto check-in: ${data.zone}`);
            if (onSuccess) onSuccess();
          } else if (data.ok && data.message === 'Already checked in today') {
            setStatus('success');
            setZone(data.zone || '');
            setMessage('Already checked in today');
          } else {
            setStatus('idle');
            setMessage(data.message || 'Not in a geofence zone');
          }
        } catch (err) {
          const detail = err.response?.data?.detail || err.response?.data?.message || 'Auto check-in unavailable';
          setStatus('idle');
          setMessage(detail);
        }
      },
      () => {
        setStatus('error');
        setMessage('Location access denied');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onSuccess]);

  return (
    <Card className={cn("border-gray-border", status === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200')} data-testid="auto-checkin-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-full flex items-center justify-center",
              status === 'success' ? "bg-emerald-100" : status === 'detecting' ? "bg-blue-100 animate-pulse" : "bg-blue-100"
            )}>
              <MapPin className={cn("h-5 w-5", status === 'success' ? "text-emerald-600" : "text-blue-600")} />
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary">
                {status === 'success' ? 'Checked In' : 'GPS Auto Check-in'}
              </p>
              <p className="text-[10px] text-gray-500">
                {message || 'Tap to auto check-in via your current GPS location'}
              </p>
              {zone && status === 'success' && (
                <Badge className="bg-emerald-100 text-emerald-700 text-[9px] mt-1">{zone}</Badge>
              )}
            </div>
          </div>
          {status !== 'success' && (
            <Button size="sm" onClick={attemptAutoCheckin} disabled={status === 'detecting'}
              className={status === 'detecting' ? '' : 'bg-blue-600 hover:bg-blue-700'}
              data-testid="auto-checkin-btn">
              {status === 'detecting' ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <MapPin className="h-3.5 w-3.5 mr-1" />}
              {status === 'detecting' ? 'Detecting...' : 'Auto Check-in'}
            </Button>
          )}
          {status === 'success' && <CheckCircle2 className="h-6 w-6 text-emerald-500" />}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Live Attendance Tab ──────────────────────────────────────────────────────
function LiveAttendance({ onRefresh }) {
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/hr/attendance/today`);
      setToday(data);
    } catch {
      // Silently handle — endpoint may not exist on older backend versions
      setToday({ date: new Date().toISOString().split('T')[0], total_employees: 0, present: 0, absent: 0, records: [], absent_employees: [] });
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) { resolve({ latitude: null, longitude: null }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({ latitude: null, longitude: null }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const handleCheckIn = async (employeeId) => {
    setCheckingIn(employeeId);
    try {
      const loc = await getLocation();
      await axios.post(`${API}/api/hr/attendance/checkin?employee_id=${employeeId}`, {
        latitude: loc.latitude, longitude: loc.longitude, method: loc.latitude ? 'gps_auto' : 'manual',
      });
      toast.success('Checked in successfully');
      fetchToday();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Check-in failed'); }
    finally { setCheckingIn(null); }
  };

  const handleCheckOut = async (employeeId) => {
    setCheckingIn(employeeId);
    try {
      const loc = await getLocation();
      await axios.post(`${API}/api/hr/attendance/checkout?employee_id=${employeeId}`, {
        latitude: loc.latitude, longitude: loc.longitude, method: loc.latitude ? 'gps_auto' : 'manual',
      });
      toast.success('Checked out successfully');
      fetchToday();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Check-out failed'); }
    finally { setCheckingIn(null); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>;

  const present = today?.records || [];
  const absent = today?.absent_employees || [];

  return (
    <div className="space-y-4" data-testid="live-attendance">
      <AutoCheckInWidget onSuccess={fetchToday} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Staff" value={today?.total_employees || 0} color="blue" />
        <StatCard icon={CheckCircle2} label="Present" value={today?.present || 0} color="emerald" />
        <StatCard icon={XCircle} label="Absent" value={today?.absent || 0} color="red" />
        <StatCard icon={Clock} label="Date" value={today?.date || new Date().toISOString().split('T')[0]} color="purple" />
      </div>

      {present.length > 0 && (
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Present Today ({present.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {present.map((r, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50/50 border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-emerald-700">{(r.employee_name || '?')[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{r.employee_name}</p>
                    <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                      <span className="capitalize">{r.department}</span>
                      <span className="flex items-center gap-0.5"><LogIn className="h-2.5 w-2.5" /> {r.check_in_time ? new Date(r.check_in_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '—'}</span>
                      {r.check_out_time && <span className="flex items-center gap-0.5"><LogOut className="h-2.5 w-2.5" /> {new Date(r.check_out_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>}
                      {r.hours_worked > 0 && <span>{r.hours_worked.toFixed(1)}h</span>}
                      {r.check_in_method === 'gps_auto' && <Badge className="bg-blue-100 text-blue-600 text-[8px] h-3.5 px-1">GPS</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.is_late && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Late</Badge>}
                  {r.check_in_time && !r.check_out_time && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleCheckOut(r.employee_id)} disabled={checkingIn === r.employee_id} data-testid={`checkout-${r.employee_id}`}>
                      {checkingIn === r.employee_id ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <LogOut className="h-3 w-3 mr-1" />} Check Out
                    </Button>
                  )}
                  {r.check_out_time && <Badge className="bg-gray-100 text-gray-600 text-[10px]">Completed</Badge>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {absent.length > 0 && (
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><XCircle className="h-4 w-4 text-red-400" /> Not Checked In ({absent.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {absent.map((emp, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-500">{(emp.first_name || '?')[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{emp.first_name} {emp.last_name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{emp.department} {emp.position ? `- ${emp.position}` : ''}</p>
                  </div>
                </div>
                <Button size="sm" className="h-7 text-xs bg-emerald-brand hover:bg-emerald-600"
                  onClick={() => handleCheckIn(emp.id)} disabled={checkingIn === emp.id} data-testid={`checkin-${emp.id}`}>
                  {checkingIn === emp.id ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <LogIn className="h-3 w-3 mr-1" />} Check In
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {present.length === 0 && absent.length === 0 && (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-medium">No employees registered yet. Add employees in Team & People first.</p>
        </CardContent></Card>
      )}
    </div>
  );
}

// ─── Attendance Records Tab ──────────────────────────────────────────────────
function AttendanceRecords() {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const [recRes, statRes] = await Promise.all([
        axios.get(`${API}/api/hr/attendance`, { params: { date_from: dateFrom, date_to: dateTo, limit: 200 } }),
        axios.get(`${API}/api/hr/attendance/stats`, { params: { date_from: dateFrom, date_to: dateTo } }),
      ]);
      setRecords(recRes.data);
      setStats(statRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  return (
    <div className="space-y-4" data-testid="attendance-records">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40 mt-1 h-8 text-sm" data-testid="att-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40 mt-1 h-8 text-sm" data-testid="att-date-to" />
        </div>
        <Button size="sm" variant="outline" onClick={fetchRecords} className="h-8"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Calendar} label="Total Records" value={stats.total_records} color="blue" />
          <StatCard icon={Clock} label="Avg Hours/Day" value={stats.by_employee?.length > 0 ? (stats.by_employee.reduce((s, e) => s + (e.total_hours || 0), 0) / Math.max(stats.total_records, 1)).toFixed(1) : '0'} color="purple" />
          <StatCard icon={Timer} label="Total Overtime" value={`${stats.by_employee?.reduce((s, e) => s + (e.total_overtime || 0), 0).toFixed(1)}h`} color="amber" />
          <StatCard icon={AlertCircle} label="Late Days" value={stats.by_employee?.reduce((s, e) => s + (e.late_count || 0), 0)} color="red" />
        </div>
      )}

      {loading ? <div className="h-40 bg-gray-100 rounded-xl animate-pulse" /> : records.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <Calendar className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-medium">No attendance records for this period</p>
        </CardContent></Card>
      ) : (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="attendance-table">
                <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Date</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Employee</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Department</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Check In</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Check Out</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Hours</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Overtime</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Status</th>
                </tr></thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-2 px-3 text-xs">{r.date}</td>
                      <td className="py-2 px-3 text-xs font-medium">{r.employee_name}</td>
                      <td className="py-2 px-3 text-xs text-gray-500 capitalize">{r.department}</td>
                      <td className="py-2 px-3 text-xs">{r.check_in_time ? new Date(r.check_in_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '—'}</td>
                      <td className="py-2 px-3 text-xs">{r.check_out_time ? new Date(r.check_out_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '—'}</td>
                      <td className="py-2 px-3 text-xs font-medium">{(r.hours_worked || 0).toFixed(1)}h</td>
                      <td className="py-2 px-3 text-xs">{r.overtime_hours > 0 ? <Badge className="bg-amber-100 text-amber-700 text-[10px]">{r.overtime_hours.toFixed(1)}h OT</Badge> : '—'}</td>
                      <td className="py-2 px-3">{r.is_late ? <Badge className="bg-red-100 text-red-600 text-[10px]">Late</Badge> : <Badge className="bg-emerald-100 text-emerald-600 text-[10px]">On Time</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Payroll Tab ─────────────────────────────────────────────────────────────
function PayrollView() {
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/hr/payroll`, { params: { month } }); setPayroll(data); }
    catch { setPayroll({ month, employees: 0, total_payroll: 0, currency: 'USD', ot_multiplier: 1.5, holiday_multiplier: 2.0, payroll: [] }); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  return (
    <div className="space-y-4" data-testid="payroll-view">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44 mt-1 h-8 text-sm" data-testid="payroll-month" />
        </div>
        <Button size="sm" variant="outline" onClick={fetchPayroll} className="h-8"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Recalculate</Button>
      </div>

      {payroll && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={DollarSign} label="Total Payroll" value={`${payroll.currency} ${payroll.total_payroll.toLocaleString()}`} color="emerald" />
          <StatCard icon={Users} label="Employees" value={payroll.employees} color="blue" />
          <StatCard icon={TrendingUp} label="OT Multiplier" value={`${payroll.ot_multiplier}x`} sub="Regular rate" color="amber" />
          <StatCard icon={Calendar} label="Period" value={payroll.month} color="purple" />
        </div>
      )}

      {loading ? <div className="h-40 bg-gray-100 rounded-xl animate-pulse" /> : !payroll || payroll.payroll.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <DollarSign className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-medium">No payroll data for {month}</p>
          <p className="text-[10px] text-gray-400 mt-1">Payroll is auto-computed from attendance records</p>
        </CardContent></Card>
      ) : (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payroll-table">
                <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Employee</th>
                  <th className="text-left text-[10px] text-gray-500 uppercase py-2.5 px-3">Department</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3">Days</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3">Hours</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3">OT Hours</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3">Base Salary</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3">Regular Pay</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3">OT Pay</th>
                  <th className="text-right text-[10px] text-gray-500 uppercase py-2.5 px-3 font-bold">Gross Pay</th>
                  <th className="w-8"></th>
                </tr></thead>
                <tbody>
                  {payroll.payroll.map((p, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === i ? null : i)}>
                      <td className="py-2.5 px-3 text-xs font-medium">{p.employee_name}</td>
                      <td className="py-2.5 px-3 text-xs text-gray-500 capitalize">{p.department}</td>
                      <td className="py-2.5 px-3 text-xs text-right">{p.days_worked}</td>
                      <td className="py-2.5 px-3 text-xs text-right">{p.total_hours.toFixed(1)}</td>
                      <td className="py-2.5 px-3 text-xs text-right">{p.overtime_hours > 0 ? <span className="text-amber-600">{p.overtime_hours.toFixed(1)}</span> : '0'}</td>
                      <td className="py-2.5 px-3 text-xs text-right">{p.base_salary.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-xs text-right">{p.regular_pay.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-xs text-right text-amber-600">{p.overtime_pay > 0 ? p.overtime_pay.toLocaleString() : '—'}</td>
                      <td className="py-2.5 px-3 text-xs text-right font-bold text-emerald-700">{payroll.currency} {p.gross_pay.toLocaleString()}</td>
                      <td className="py-2.5 px-1">{expandedRow === i ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-emerald-50/50 border-t-2 border-emerald-200">
                    <td colSpan={8} className="py-2.5 px-3 text-xs font-bold text-right">Total Payroll</td>
                    <td className="py-2.5 px-3 text-sm font-bold text-right text-emerald-700">{payroll.currency} {payroll.total_payroll.toLocaleString()}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Overtime Tab ────────────────────────────────────────────────────────────
function OvertimeView() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], hours: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchOT = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/hr/overtime`); setRequests(data); }
    catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOT(); }, [fetchOT]);

  const handleSubmit = async () => {
    if (!form.hours || parseFloat(form.hours) <= 0) { toast.error('Enter valid hours'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/hr/overtime`, { date: form.date, hours: parseFloat(form.hours), reason: form.reason });
      toast.success('Overtime request submitted');
      setShowNew(false);
      setForm({ date: new Date().toISOString().split('T')[0], hours: '', reason: '' });
      fetchOT();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleAction = async (otId, action) => {
    try {
      await axios.put(`${API}/api/hr/overtime/${otId}/${action}`);
      toast.success(`Overtime ${action}d`);
      fetchOT();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const statusColors = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-4" data-testid="overtime-view">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">Overtime Requests</h3>
          <p className="text-[10px] text-gray-400">Submit and manage overtime claims</p>
        </div>
        <Button size="sm" className="bg-emerald-brand hover:bg-emerald-600" onClick={() => setShowNew(true)} data-testid="new-overtime-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Request
        </Button>
      </div>

      {loading ? <div className="h-20 bg-gray-100 rounded-xl animate-pulse" /> : requests.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-8 text-center">
          <Timer className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-medium">No overtime requests</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {requests.map(r => (
            <div key={r.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-border hover:border-gray-300 transition-all">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-text-primary">{r.employee_name}</p>
                  <Badge className={cn("text-[10px]", statusColors[r.status])}>{r.status}</Badge>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">{r.date} - {r.hours}h overtime {r.reason ? `- ${r.reason}` : ''}</p>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-emerald-600 border-emerald-200" onClick={() => handleAction(r.id, 'approve')} data-testid={`approve-ot-${r.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600 border-red-200" onClick={() => handleAction(r.id, 'reject')} data-testid={`reject-ot-${r.id}`}>Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-sm">New Overtime Request</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1 h-8 text-sm" data-testid="ot-date" /></div>
            <div><Label className="text-xs">Hours (max 12)</Label><Input type="number" min="0.5" max="12" step="0.5" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} placeholder="e.g. 2.5" className="mt-1 h-8 text-sm" data-testid="ot-hours" /></div>
            <div><Label className="text-xs">Reason</Label><Textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Optional reason..." className="mt-1 text-sm min-h-[60px]" /></div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-brand hover:bg-emerald-600" onClick={handleSubmit} disabled={submitting} data-testid="submit-ot-btn">{submitting ? 'Submitting...' : 'Submit'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Attendance Rules Tab ────────────────────────────────────────────────────
function AttendanceRulesView() {
  const [rules, setRules] = useState(null);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddZone, setShowAddZone] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: '', zone_type: 'office', latitude: '', longitude: '', radius_meters: 200, address: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, geoRes] = await Promise.all([
        axios.get(`${API}/api/hr/attendance/rules`),
        axios.get(`${API}/api/hr/attendance/geofences`),
      ]);
      setRules(rulesRes.data);
      setGeofences(geoRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveRules = async (updates) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/hr/attendance/rules`, { ...rules, ...updates });
      setRules(data);
      toast.success('Rules updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addZone = async () => {
    if (!zoneForm.name || !zoneForm.latitude || !zoneForm.longitude) { toast.error('Name and coordinates required'); return; }
    try {
      await axios.post(`${API}/api/hr/attendance/geofences`, { ...zoneForm, latitude: parseFloat(zoneForm.latitude), longitude: parseFloat(zoneForm.longitude) });
      toast.success('Geofence zone added');
      setShowAddZone(false);
      setZoneForm({ name: '', zone_type: 'office', latitude: '', longitude: '', radius_meters: 200, address: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteZone = async (zoneId) => {
    try { await axios.delete(`${API}/api/hr/attendance/geofences/${zoneId}`); toast.success('Zone removed'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;

  const ZONE_TYPES = [
    { value: 'office', label: 'Office' }, { value: 'client_site', label: 'Client Site' },
    { value: 'warehouse', label: 'Warehouse' }, { value: 'custom', label: 'Custom' },
  ];
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="space-y-4" data-testid="attendance-rules">
      {/* Working Hours */}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-blue-500" /> Working Hours</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Start Time</Label><Input type="time" value={rules?.work_start_time || '09:00'} onChange={e => saveRules({ work_start_time: e.target.value })} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">End Time</Label><Input type="time" value={rules?.work_end_time || '18:00'} onChange={e => saveRules({ work_end_time: e.target.value })} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Late Threshold (min)</Label><Input type="number" value={rules?.late_threshold_minutes || 15} onChange={e => saveRules({ late_threshold_minutes: parseInt(e.target.value) || 15 })} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">OT Multiplier</Label><Input type="number" step="0.1" value={rules?.overtime_multiplier || 1.5} onChange={e => saveRules({ overtime_multiplier: parseFloat(e.target.value) || 1.5 })} className="mt-1 h-8 text-sm" /></div>
        </CardContent>
      </Card>

      {/* Rules by Work Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-emerald-500" /> Office-Based</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.office_rules?.geofence_auto_checkin ?? true} onChange={e => saveRules({ office_rules: { ...rules?.office_rules, geofence_auto_checkin: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> Auto check-in when entering geofence</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.office_rules?.require_gps ?? true} onChange={e => saveRules({ office_rules: { ...rules?.office_rules, require_gps: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> Require GPS verification</label>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-blue-500" /> Field-Based</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.field_rules?.time_based_checkin ?? true} onChange={e => saveRules({ field_rules: { ...rules?.field_rules, time_based_checkin: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> Auto check-in at work start time</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.field_rules?.match_client_location ?? true} onChange={e => saveRules({ field_rules: { ...rules?.field_rules, match_client_location: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> Match client geolocation</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.field_rules?.gps_required ?? true} onChange={e => saveRules({ field_rules: { ...rules?.field_rules, gps_required: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> GPS tracking required</label>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-purple-500" /> Remote-Based</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.remote_rules?.time_based_checkin ?? true} onChange={e => saveRules({ remote_rules: { ...rules?.remote_rules, time_based_checkin: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> Auto check-in at scheduled time</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.remote_rules?.ip_validation ?? false} onChange={e => saveRules({ remote_rules: { ...rules?.remote_rules, ip_validation: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> IP address validation</label>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-amber-500" /> Hybrid</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={rules?.hybrid_rules?.follow_schedule ?? true} onChange={e => saveRules({ hybrid_rules: { ...rules?.hybrid_rules, follow_schedule: e.target.checked } })} className="w-3.5 h-3.5 rounded" /> Follow office/remote schedule</label>
            <div><Label className="text-[10px] text-gray-400">Office Days</Label>
              <div className="flex flex-wrap gap-1 mt-1">{DAYS.map(d => {
                const isOffice = (rules?.hybrid_rules?.office_days || []).includes(d);
                return <button key={d} onClick={() => {
                  const days = isOffice ? (rules?.hybrid_rules?.office_days || []).filter(x => x !== d) : [...(rules?.hybrid_rules?.office_days || []), d];
                  saveRules({ hybrid_rules: { ...rules?.hybrid_rules, office_days: days, remote_days: DAYS.filter(x => !days.includes(x)) } });
                }} className={cn("text-[10px] px-2 py-0.5 rounded-full border", isOffice ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-400 border-gray-200")}>{d.slice(0, 3)}</button>;
              })}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Geofence Zones */}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-red-500" /> Geofence Zones ({geofences.length})</CardTitle>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowAddZone(true)} data-testid="add-geofence-btn"><Plus className="h-3 w-3" /> Add Zone</Button>
          </div>
        </CardHeader>
        <CardContent>
          {geofences.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No geofence zones configured. Add your office location to enable auto-attendance.</p>
          ) : (
            <div className="space-y-2">
              {geofences.map(z => (
                <div key={z.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <p className="text-sm font-medium">{z.name}</p>
                    <p className="text-[10px] text-gray-400">{z.address || `${z.latitude}, ${z.longitude}`} — {z.radius_meters}m radius</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 text-[10px] capitalize">{z.zone_type?.replace('_', ' ')}</Badge>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => deleteZone(z.id)}><XCircle className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Zone Dialog */}
      <Dialog open={showAddZone} onOpenChange={setShowAddZone}>
        <DialogContent className="max-w-sm z-[9999]">
          <DialogHeader><DialogTitle className="text-sm font-heading">Add Geofence Zone</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Zone Name *</Label><Input value={zoneForm.name} onChange={e => setZoneForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Main Office" className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Zone Type</Label>
              <Select value={zoneForm.zone_type} onValueChange={v => setZoneForm(f => ({ ...f, zone_type: v }))}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[10000]">{ZONE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Latitude *</Label><Input type="number" step="any" value={zoneForm.latitude} onChange={e => setZoneForm(f => ({ ...f, latitude: e.target.value }))} className="mt-1 h-8 text-sm" /></div>
              <div><Label className="text-xs">Longitude *</Label><Input type="number" step="any" value={zoneForm.longitude} onChange={e => setZoneForm(f => ({ ...f, longitude: e.target.value }))} className="mt-1 h-8 text-sm" /></div>
            </div>
            <Button size="sm" variant="link" className="text-[10px] text-blue-600 p-0 h-auto" onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(p => {
                  setZoneForm(f => ({ ...f, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) }));
                  toast.success('Location captured');
                }, () => toast.error('Could not get location'));
              }
            }}>Use my current location</Button>
            <div><Label className="text-xs">Radius (meters)</Label><Input type="number" value={zoneForm.radius_meters} onChange={e => setZoneForm(f => ({ ...f, radius_meters: parseInt(e.target.value) || 200 }))} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Address</Label><Input value={zoneForm.address} onChange={e => setZoneForm(f => ({ ...f, address: e.target.value }))} placeholder="Optional street address" className="mt-1 h-8 text-sm" /></div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowAddZone(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-brand hover:bg-emerald-600" onClick={addZone}>Add Zone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function AttendancePayrollPage() {
  return (
    <div className="space-y-6 animate-fadeIn" data-testid="attendance-payroll-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
          Attendance & Payroll<PageInfoTooltip pageKey="attendance-payroll" />
        </h1>
        <p className="text-gray-medium text-sm">GPS-based attendance tracking, overtime management, and automated payroll</p>
      </div>

      <Tabs defaultValue="live" className="space-y-4">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="live" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-live"><MapPin className="h-3.5 w-3.5" /> Live</TabsTrigger>
          <TabsTrigger value="records" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-records"><Calendar className="h-3.5 w-3.5" /> Records</TabsTrigger>
          <TabsTrigger value="payroll" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-payroll"><DollarSign className="h-3.5 w-3.5" /> Payroll</TabsTrigger>
          <TabsTrigger value="overtime" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-overtime"><Timer className="h-3.5 w-3.5" /> Overtime</TabsTrigger>
          <TabsTrigger value="rules" className="text-xs sm:text-sm whitespace-nowrap gap-1.5" data-testid="tab-rules"><Settings className="h-3.5 w-3.5" /> Rules & Geofence</TabsTrigger>
        </TabsList>

        <TabsContent value="live"><LiveAttendance /></TabsContent>
        <TabsContent value="records"><AttendanceRecords /></TabsContent>
        <TabsContent value="payroll"><PayrollView /></TabsContent>
        <TabsContent value="overtime"><OvertimeView /></TabsContent>
        <TabsContent value="rules"><AttendanceRulesView /></TabsContent>
      </Tabs>
    </div>
  );
}
